import * as Services from "../../Services";
import {call, put, select, takeLatest} from "@redux-saga/core/effects";
import {
  CANCEL_PICKUP,
  DELETE_SHIPMENT,
  GET_PICKUPS,
  GET_SHIPMENTS_BY_PICKUP,
  GET_UNITS_BY_SHIPMENT,
  IPickupSearch,
  IShipmentSearch,
  SAVE_PICKUP,
  UPDATE_PICKUP
} from "./Types";
import {RootState} from "../Reducers";
import {PickupActions} from "./Slice";
import {ModalActions} from "../Modal/Slice";
import {
  comparator,
  getCustomerValue,
  setLoadingPending,
  setLoadingRejected,
  setLoadingResolve,
  toast
} from "../../Utils/Utils";
import Pickup from "../../Models/Pickup";
import PickupTypes from "../../Models/PickupTypes";
import {
  LOADING_CANCEL_SHIPMENT,
  LOADING_EDIT_SHIPMENT,
  LOADING_PICKUPS,
  LOADING_SAVE_PICKUP,
  LOADING_SHIPMENTS,
  LOADING_UNITS
} from "../Loading/Types";
import NewPickup from "../../Models/NewPickup";
import Shipment from "../../Models/Shipment";
import Unit from "../../Models/Unit";
import {CANCEL_SHIPMENT_MODAL, EDIT_PICKUP_MODAL, PICKUP_MODAL, SHIPPING_DETAIL_MODAL} from "../Modal/Types";
import {PayloadAction} from "@reduxjs/toolkit";

function* classifyPickups(pickups: Pickup[]) {
  const pending = pickups.filter(p => p.status.toLocaleLowerCase() === PickupTypes.PENDIENTE.toLocaleLowerCase())
    .sort(comparator("express", { asBoolean: true } ));

  const scheduled = pickups.filter(p => p.status.toLocaleLowerCase() === PickupTypes.AGENDADA.toLocaleLowerCase())
    .sort(comparator("express", { asBoolean: true } ));

  const pickup = pickups.filter(p => p.status.toLocaleLowerCase() === PickupTypes.RECOGIDA.toLocaleLowerCase())
    .sort(comparator("express", { asBoolean: true } ));

  const canceled = pickups.filter(
    p => p.status.toLocaleLowerCase() === PickupTypes.CANCELADA.toLocaleLowerCase() ||
      p.status.toLowerCase() === PickupTypes.INCOMPLETA.toLocaleLowerCase())
    .sort(comparator("express", { asBoolean: true } ));

  yield put(PickupActions.setPickupsPending(pending));
  yield put(PickupActions.setPickupsScheduled(scheduled));
  yield put(PickupActions.setPickupsPickup(pickup));
  yield put(PickupActions.setPickupsCancel(canceled));
}

function* getPickups({ payload }: PayloadAction<IPickupSearch|undefined>) {
  try {
    yield put(setLoadingPending(LOADING_PICKUPS));
    const { CompanyReducer : {company_selected, branch_selected} ,
      FilterReducer : {dashboardFilters},
      LoadingReducer: {loadingPickups}
    } = yield select((state: RootState) => state);

    let customer = getCustomerValue(company_selected, branch_selected);

    const allFalse = Object.keys(dashboardFilters).every(key => !dashboardFilters[key]);

    const runPickup = allFalse || dashboardFilters.shippingPending || dashboardFilters.shippingSchedule;
    const runShipment = allFalse || dashboardFilters.shippingPickup || dashboardFilters.shipmentInTransit;

    if (runPickup) {
      const resPickups = yield call(Services.getPickups, customer, dashboardFilters);
      if (resPickups?.success) {
        const pickups = resPickups.results as Array<Pickup>;
        yield put(PickupActions.setPickups(pickups));
        yield classifyPickups(pickups);
        if (!runShipment) {
          yield put(setLoadingResolve(LOADING_PICKUPS));
        }
      } else {
        throw Error(resPickups.message || 'There was an error.');
      }
    } else {
      yield put(PickupActions.setPickups([]));
    }

    if (runShipment) {
      const resShipments = yield call(Services.getShipmentsWithFilters, customer, dashboardFilters);
      if (resShipments?.success) {
        const shipments = resShipments.results as Array<Shipment>;
        yield put(PickupActions.setShipments(shipments));
        if (loadingPickups.status.isLoading()) {
          yield put(setLoadingResolve(LOADING_PICKUPS));
        }
      } else {
        throw Error(resShipments.message || 'There was an error.');
      }
    } else {
      yield put(PickupActions.setShipments([]));
    }

  } catch (e) {
    console.log(e);
    toast(e?.message, "error");
    yield put(setLoadingRejected(LOADING_PICKUPS,  e.message));
  }
}

function* updatePickup({payload}: PayloadAction<Pickup>) {
  try {
    yield put(setLoadingPending(LOADING_EDIT_SHIPMENT));
    const res = yield call(Services.updatePickup, payload as Pickup);
    if (!res?.success) {
      throw Error('There was an error.');
    } else {
      yield put(setLoadingResolve(LOADING_EDIT_SHIPMENT));
      yield put(PickupActions.getPickups());
      yield put(ModalActions.closeModal({modal:EDIT_PICKUP_MODAL}));
      yield put(ModalActions.closeModal({modal:SHIPPING_DETAIL_MODAL}));
      toast('Envio actualizado');
    }
  } catch (e) {
    yield put(setLoadingRejected(LOADING_EDIT_SHIPMENT,  e.message));
    toast(e.message, "error");
  }
}

function* savePickup({payload}: PayloadAction<NewPickup>) {
  try {
    yield put(setLoadingPending(LOADING_SAVE_PICKUP));
    const params : any = { ...payload };
    params.status = 'Pendiente';
    params.from_customer = true;
    const res = yield call(Services.savePickup, params);
    yield put(setLoadingResolve(LOADING_SAVE_PICKUP));

    if (!res?.success) {
      throw Error('There was an error.');
    } else {
      toast("envio creado");
      yield call(getPickups, {} as PayloadAction<IPickupSearch>);
      yield put(ModalActions.closeModal({
        modal: PICKUP_MODAL
      }));
    }
  } catch (e) {
    console.log(e)
    toast(e.message, "error");
    yield put(setLoadingRejected(LOADING_SAVE_PICKUP, e.message));
  }
}

function* getShipmentsByPickups({payload}: PayloadAction<IShipmentSearch>) {
  try {
    yield put(setLoadingPending(LOADING_SHIPMENTS));
    const res = yield call(Services.getShipmentsByPickup, payload);
    yield put(setLoadingResolve(LOADING_SHIPMENTS));

    if (!res?.success) {
      throw Error('There was an error.');
    } else {
      const shipments = res.results as Shipment[];
      yield put(PickupActions.setShipmentsByPickup(shipments))
    }
  } catch (e) {
    toast(e.message, "error");
    yield put(setLoadingRejected(LOADING_SHIPMENTS, e.message));
  }
}

function* deleteShipment({payload}: PayloadAction<Shipment>) {
  try {
    const res = yield call(Services.deleteShipment, payload._KEY);
    console.log(res);
    if (!res?.success) {
      throw Error('There was an error.');
    }
    else {
      yield call(getShipmentsByPickups, { payload: { pickup: payload.pickup } } as PayloadAction<IShipmentSearch>)
    }
  } catch (e) {
    toast(e.message, "error");
  }
}

function* getUnitsByShipment({payload}: PayloadAction<Shipment>) {
  try {
    yield put(setLoadingPending(LOADING_UNITS));
    const res = yield call(Services.getUnitsByShipment, payload._KEY);
    yield put(setLoadingResolve(LOADING_UNITS));

    if (!res?.success) {
      throw Error('There was an error.');
    } else {
      const units = res.results as Unit[];
      yield put(PickupActions.setUnitsByShipment(units))
    }
  } catch (e) {
    toast(e.message, "error");
    yield put(setLoadingRejected(LOADING_UNITS, e.message));
  }
}

function* cancelPickup({payload}:PayloadAction<Pickup>) {
  try {
    yield put(setLoadingPending(LOADING_CANCEL_SHIPMENT));
    const res = yield call(Services.updatePickup, {_KEY:payload?._KEY, status:PickupTypes.CANCELADA, comment:payload?.comment} as Pickup);
    if (!res?.success) {
      throw Error('There was an error.');
    } else {
      yield put(PickupActions.getPickups());
      yield put(ModalActions.closeModal({modal:CANCEL_SHIPMENT_MODAL}));
      yield put(ModalActions.closeModal({modal:SHIPPING_DETAIL_MODAL}));
      yield put(setLoadingResolve(LOADING_CANCEL_SHIPMENT));
      toast('Envio cancelado');
    }
  } catch (e) {
    yield put(setLoadingRejected(LOADING_CANCEL_SHIPMENT,  e.message));
    toast(e.message, "error");
  }
}

export function* pickupSaga() {
  yield takeLatest(GET_PICKUPS, getPickups);
  yield takeLatest(UPDATE_PICKUP, updatePickup);
  yield takeLatest(SAVE_PICKUP, savePickup);
  yield takeLatest(GET_SHIPMENTS_BY_PICKUP, getShipmentsByPickups);
  yield takeLatest(DELETE_SHIPMENT, deleteShipment);
  yield takeLatest(GET_UNITS_BY_SHIPMENT, getUnitsByShipment);
  yield takeLatest(CANCEL_PICKUP, cancelPickup);
}
